import * as React from "react";
import { useNavigate } from "react-router-dom";
import { notification, Modal, Input, InputRef } from "antd";
import { nameOfKind, request, getName, pluralNameOfKind, homeLink } from "../../services/cpln";
import { isApex, sleep } from "../../services/utils";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchHasWorkload } from "../../store/userDataSlice";
import { Kind } from "../../schema/types/base";
import { selectOrg } from "../../store/contextSlice";
import { invalidateCacheForKind } from "../../store/api/cacheInvalidation";

interface Props {
  kind: string;
  itemName?: string;
  links: string[];
  onCancel: any;
  onComplete: any;
}

export const DeleteModal: React.FC<Props> = ({ kind, itemName, links, onCancel, onComplete }) => {
  const dispatch = useAppDispatch();
  const org = useAppSelector(selectOrg);

  const navigate = useNavigate();

  const [confirmationText, setConfirmationText] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const inputRef = React.useRef<InputRef>(null);

  const CONFIRMATION_TOKEN = React.useMemo(() => {
    if (itemName) {
      return itemName;
    }
    return `Selected ${pluralNameOfKind(kind)}`;
  }, [itemName, kind]);

  async function confirmDelete() {
    try {
      setIsLoading(true);
      const navigation = homeLink(kind as Kind, { org, gvc: "none" });

      let results: PromiseSettledResult<any>[];

      if (kind === "domain") {
        const apexDomains: string[] = [];
        const subDomains: string[] = [];
        for (const link of links) {
          const name = link.split(`/${kind}/`)[1];
          if (isApex(name)) {
            apexDomains.push(link);
          } else {
            subDomains.push(link);
          }
        }
        const batch1Results = await Promise.allSettled(
          subDomains.map((link) => request({ method: "delete", url: link }))
        );
        let batch2Results: PromiseSettledResult<any>[] = [];
        if (apexDomains.length > 0) {
          await sleep(2);
          batch2Results = await Promise.allSettled(
            apexDomains.map((link) => request({ method: "delete", url: link }))
          );
        }
        results = [...batch1Results, ...batch2Results];
      } else {
        results = await Promise.allSettled(
          links.map((link) => request({ method: "delete", url: link }))
        );
      }

      const succeeded = results.filter((r) => r.status === "fulfilled");
      const failed = results.filter((r) => r.status === "rejected");

      if (succeeded.length > 0) {
        invalidateCacheForKind(dispatch, kind, links);
      }

      if (kind === "gvc" || kind === "workload") {
        await dispatch(fetchHasWorkload());
      }

      if (succeeded.length > 0) {
        notification.success({
          message: "Success",
          description: `Deleted ${succeeded.length} Item${succeeded.length > 1 ? "s" : ""}`,
        });
      }

      if (failed.length > 0) {
        const firstError = failed[0] as PromiseRejectedResult;
        const errorMessage = firstError.reason?.response?.data?.message || firstError.reason?.message || "Unknown error";
        const hasDetails =
          firstError.reason?.response?.data?.details?.usedBy && firstError.reason.response.data.details.usedBy.length > 0;
        notification.warning({
          message: `Failed to delete ${failed.length} item${failed.length > 1 ? "s" : ""}`,
          description: hasDetails ? (
            <div>
              <span>{errorMessage}</span>
              <ul>
                {firstError.reason.response.data.details.usedBy.map((link: string) => (
                  <li className="cursor-pointer" onClick={() => navigate(`/console${link}`)}>
                    {link}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <span>{errorMessage}</span>
          ),
        });
      }

      if (succeeded.length > 0) {
        onComplete();
        navigate(`/console${navigation}`);
      }
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed to delete",
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  }

  let shouldConfirmDeletion = false;
  let canDelete = true;
  if (["volumeset", "workload", "identity", "mk8s", "secret"].includes(kind)) {
    shouldConfirmDeletion = true;
    if (confirmationText !== CONFIRMATION_TOKEN) {
      canDelete = false;
    }
  }

  return (
    <Modal
      title={
        <>
          Delete {nameOfKind(kind)}:{" "}
          <span className="font-bold" style={{ color: "var(--color-danger)" }}>
            {links.length < 2 ? getName(links[0]) : `${links.length} Items`}
          </span>
        </>
      }
      open={true}
      maskClosable={false}
      onCancel={onCancel}
      onOk={confirmDelete}
      cancelText={`Cancel`}
      okText={`Delete`}
      cancelButtonProps={{ type: "default", disabled: isLoading }}
      okButtonProps={{ type: "primary", danger: true, disabled: isLoading || !canDelete, loading: isLoading }}
      destroyOnHidden
      afterOpenChange={(open) => {
        if (open && inputRef.current) {
          inputRef.current.focus();
        }
      }}
    >
      <div className="flex flex-col gap-2">
        <div>
          Are you sure you want to delete {links.length > 1 ? "these" : "this"}{" "}
          {links.length > 1 ? pluralNameOfKind(kind) : nameOfKind(kind)}? This action cannot be undone.
        </div>
        {shouldConfirmDeletion ? (
          <>
            <div className="delete-modal-confirmation-warning mt-4">{`Type "${CONFIRMATION_TOKEN}" for confirming the delete action`}</div>
            <Input
              ref={inputRef}
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && canDelete && !isLoading) {
                  confirmDelete();
                }
              }}
              placeholder={`confirmation`}
            />
          </>
        ) : null}
      </div>
    </Modal>
  );
};
